import { useCallback } from 'react';

import { useLanguage } from '../../contexts/lang/languageContext';

import { useTenantSlug } from '~/features/tenant/helpers';
import { LocalizationService } from '~/services/api/localization/endpoints';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';

export const useLocalizedCustomFieldData = () => {
  const slug = useTenantSlug();
  const { selectedLanguage } = useLanguage();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const localizedCustomFieldQuery =
    LocalizationService.endpoints.reportCustomFields.useQuery(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
      },
      { skip: !slug || !selectedChannel },
    );

  const getLocalizedCustomFieldValues = useCallback(
    (title: string, value: string, translate = '') => {
      if (!localizedCustomFieldQuery.isSuccess) {
        return {
          title: title,
          value: value,
        };
      }
      if (translate === 'original') {
        let translation: { title: string; dropdown_choices: string[] } = {
          title: '',
          dropdown_choices: [],
        };
        const itemFound = localizedCustomFieldQuery.data.find((item) => {
          translation = item.translations[selectedLanguage.code];
          if (!translation) return false;
          const titleMatches = translation.title === title;
          if (item.dropdown_choices.length === 0) {
            return titleMatches;
          } else {
            const localizedIndex = translation.dropdown_choices.findIndex(
              (choice) => choice === value,
            );
            return titleMatches && localizedIndex !== -1;
          }
        });

        if (!itemFound || !translation) {
          return {
            title: title,
            value: value,
          };
        }
        
        const originalValue =
          itemFound.dropdown_choices.length > 0
            ? itemFound.dropdown_choices[
                translation.dropdown_choices.findIndex(
                  (choice) => choice === value,
                )
              ]
            : value;
        return {
          title: itemFound.title,
          value: originalValue,
        };
      } else {
        const itemFound = localizedCustomFieldQuery.data.find((item) => {
          const translation = item.translations[selectedLanguage.code];
          if (!translation) return false;
          if (item.dropdown_choices.length === 0) return item.title === title;
          const index = item.dropdown_choices.indexOf(value);
          return (
            item.title === title &&
            translation.dropdown_choices.length !== 0 &&
            index !== -1
          );
        });

        if (itemFound) {
          if (itemFound.dropdown_choices.length === 0) {
            // If there are no dropdown choices, return the input value directly
            // This also indicates that the custom field is not a dropdown field
            const translation = itemFound.translations[selectedLanguage.code];
            return {
              title: translation.title,
              value,
            };
          } else {
            // This returns the localized title and value in custom dropdown field
            const translation = itemFound.translations[selectedLanguage.code];
            const index = itemFound.dropdown_choices.indexOf(value);
            return {
              title: translation.title,
              value: translation.dropdown_choices[index],
            };
          }
        } else {
          // If there is no match on the localized data return the title and value
          return {
            title,
            value,
          };
        }
      }
    },
    [
      localizedCustomFieldQuery.data,
      localizedCustomFieldQuery.isSuccess,
      selectedLanguage.code,
    ],
  );

  return { getLocalizedCustomFieldValues };
};
